import React, { FC, useState, useEffect, useRef } from 'react';

import Lockbody from 'helpers/lockbody';
import Logo from 'components/Logo';
import Button from 'components/Button';

import NavigationDropdown from './dropdown';
import NavigationItem from './item';

import {
    NavProfile,
    NavSearchButton,
    NavMoreButton,
    NavMenu,
    NavSearch,
    LogoStyled,
    MainCTA,
    SlotLeft,
    SlotRight,
    Wrapper,
    NavSecondary,
    NavCTA,
    Navigation as NavigationWrapper,
} from './styled';

export type NavigationProps = {
    /** Navigation should be light when using background images as first section or hero banners */
    light?: boolean;

    /** Should the navigation be fixed to the top */
    fixed?: boolean;

    /** Displays Yousician logo */
    showLogo?: boolean;

    /** Properties for the logo tag */
    logoProps?: any;

    /**
     * Displays a search bar on the right area on desktop.
     * Shows search button on mobile only if position is fixed
     * (there are some implementation issues when position is static)
     */
    showSearch?: boolean;

    /** Current logged in user */
    user: {
        avatar: string;
        menu?: React.ReactNode;
    } | null;

    /** Shows the user widget (login/current user) */
    showUser?: boolean;

    /** Additional properties for the login button */
    loginButtonProps?: any;

    /** Displays a CTA on the left area */
    cta?: any;

    loginLabel?: string;

    menu?: React.ReactNode;

    profileLabel?: string;

    closeMenu: boolean;

    onMenuOpen: () => void;
};

const Navigation: FC<
    NavigationProps & React.HTMLAttributes<HTMLDivElement>
> = ({
    light,
    cta,
    fixed,
    showLogo,
    logoProps,
    showUser,
    showSearch,
    menu,
    user,
    loginButtonProps,
    profileLabel,
    loginLabel,
    closeMenu,
    onMenuOpen,
    ...rest
}) => {
    const [searching, setSearching] = useState<boolean>(false);
    const [lock, setLock] = useState<boolean>(false);
    const [sticky, setSticky] = useState<boolean>(false);

    const stickyRef = useRef(sticky);
    const setStickyRef = (val: boolean) => {
        stickyRef.current = val;
        setSticky(val);
    };

    useEffect(() => {
        const onScroll = () => {
            const scrollTop = window.scrollY;
            if (scrollTop >= 50 && !stickyRef.current) {
                setStickyRef(true);
            } else if (scrollTop < 50 && stickyRef.current) {
                setStickyRef(false);
            }
        };

        window.addEventListener('scroll', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, []);

    const onSearchClick = () => {
        setSearching(!searching);
        setLock(false);
    };

    const closeMobileMenuIfOpen = () => {
        if (lock) onMoreClick();
    };

    useEffect(() => {
        if (closeMenu) closeMobileMenuIfOpen();

        // eslint-disable-next-line
    }, [closeMenu]);

    const onMoreClick = () => {
        if (searching) Lockbody();

        const lockBody = Lockbody();
        setLock(lockBody);
        setSearching(false);

        if (!lockBody) onMenuOpen();
    };

    const classes = [rest.className];

    if (light) classes.push('Nlight_sc');
    if (!fixed) classes.push('Nstatic_sc');
    if (light && fixed && sticky) classes.push('Nsticky_sc');
    if (lock) classes.push('Nlock_sc');
    if (searching) classes.push('Nsearching_sc');

    return (
        <Wrapper id="navigation" className={classes.join(' ')} {...rest}>
            <NavigationWrapper>
                <SlotLeft>
                    {showLogo && (
                        <LogoStyled {...logoProps} data-type="nav_logo">
                            <Logo className="main" />
                            <Logo className="icon" icon />
                        </LogoStyled>
                    )}

                    {cta && (
                        <MainCTA>
                            <Button
                                size="medium"
                                theme="primary"
                                {...cta.props}
                            >
                                {cta.label}
                            </Button>
                        </MainCTA>
                    )}
                </SlotLeft>

                <SlotRight>
                    <NavMenu>
                        {menu && menu}
                        {user && showUser ? (
                            <NavSecondary>
                                <NavigationDropdown
                                    label={profileLabel ? profileLabel : ''}
                                    itemChildren={
                                        <NavProfile
                                            src={user.avatar}
                                            alt="Your avatar"
                                        />
                                    }
                                >
                                    {user.menu}
                                </NavigationDropdown>
                            </NavSecondary>
                        ) : showUser ? (
                            <NavCTA>
                                <Button
                                    {...loginButtonProps}
                                    size="medium"
                                    theme="white"
                                >
                                    {loginLabel}
                                </Button>
                            </NavCTA>
                        ) : null}
                    </NavMenu>

                    {showSearch && fixed && (
                        <NavSearchButton onClick={onSearchClick} />
                    )}
                    {menu && <NavMoreButton onClick={onMoreClick} />}
                    {showSearch && (
                        <NavSearch
                            className="NavSearch_sc_desktop"
                            searchTheme="white"
                            input={{
                                placeholder: 'Search for songs',
                            }}
                        />
                    )}
                </SlotRight>
            </NavigationWrapper>
        </Wrapper>
    );
};

export default Navigation;

export { NavigationDropdown, NavigationItem };
